import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Batching, MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {HalImage} from "@cm/hal"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/reshape/definition"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {
    AnyImageOpHalData,
    CtorArgs,
    ImageOpHalData,
    ImageOpHalDataParameters,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_VERY_LOW} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpReshapeHal extends ImageOpHal<Definition, ImageOpReshapeHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpReshapeHalData>): ImageOpReshapeHalData {
        return new ImageOpReshapeHalData({
            ...args,
            descriptor: {
                ...args.parameters.sourceImage.descriptor,
                batching: this.computeBatching(args.parameters),
            },
            outputImageReuse: "no-output-image",
            updateComplexity: UPDATE_COMPLEXITY_VERY_LOW,
        })
    }

    private computeBatching(parameters: ImageOpHalDataParameters<Definition>): Batching | undefined {
        const {sourceImage, shape, options} = parameters
        let numPatches = shape.numPatches
        let patchSize = shape.patchSize
        if (!numPatches && !patchSize) {
            throw new Error("reshape: either numPatches or patchSize must be provided")
        }
        if (!numPatches) {
            numPatches = {
                x: 1,
                y: 1,
            }
        }
        if (!patchSize) {
            patchSize = {
                width: Math.ceil(sourceImage.descriptor.width / numPatches.x),
                height: Math.ceil(sourceImage.descriptor.height / numPatches.y),
            }
        } else if (!numPatches) {
            numPatches = {
                x: Math.ceil(sourceImage.descriptor.width / patchSize.width),
                y: Math.ceil(sourceImage.descriptor.height / patchSize.height),
            }
        }
        const allowPatchFill = options?.allowPatchFill ?? false
        if (!allowPatchFill) {
            if (patchSize.width * numPatches.x !== sourceImage.descriptor.width || patchSize.height * numPatches.y !== sourceImage.descriptor.height) {
                throw new Error(
                    `reshape: target image size (${sourceImage.descriptor.width}, ${sourceImage.descriptor.height}) is not a multiple of batchSize (${numPatches.x}, ${numPatches.y}) and patchSize (${patchSize.width}, ${patchSize.height})`,
                )
            }
        }
        return numPatches.x === 1 && numPatches.y === 1 ? undefined : {patchSize, numPatches}
    }
}

class ImageOpReshapeHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpReshapeHalData>) {
        super(args)
    }

    protected override async updateHalImage(mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<RegionLockedHalImageRef> {
        // TODO translate region to source image coordinates
        if (
            mipLevelRegion.mipLevel !== 0 ||
            mipLevelRegion.x !== 0 ||
            mipLevelRegion.y !== 0 ||
            mipLevelRegion.width !== this.descriptor.width ||
            mipLevelRegion.height !== this.descriptor.height
        ) {
            throw new Error("ImageOpReshapeHal source image coordinates are not yet translated properly !")
        }
        return this.parameters.sourceImage.getUpdatedHalImage(mipLevelRegion, options)
    }

    // ImageHal
    protected async writeRegion(_outputImage: HalImage, _mipLevelRegion: MipLevelRegion, _options?: UpdateImageOptions): Promise<void> {
        throw new Error("ImageOpReshapeHal does not support writeRegion, use getUpdatedHalImage instead")
    }

    protected override onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        if (
            sourceRegions.length !== 1 ||
            sourceRegions[0].x !== 0 ||
            sourceRegions[0].y !== 0 ||
            sourceRegions[0].width !== sourceImage.descriptor.width ||
            sourceRegions[0].height !== sourceImage.descriptor.height
        ) {
            ImageOpReshapeHalData.logger?.warn(
                "ImageOpReshapeHal: onSourceRegionsInvalidated for partial source regions are not properly handled yet ! Invalidating entire output image.",
            )
        }
        this.invalidateRegions()
    }
}
